import React from 'react';
import * as R from 'ramda';
import { ThunkDispatch } from 'redux-thunk';
import { Action } from 'redux';

import { Customer } from 'types/Customer';
import {
  getCustomerSearchResultsConfiguration,
  getCustomerSearchResultsSortingConfiguration,
} from 'reducers/configs/settings';
import { RootState } from 'reducers';

import { NameRow, CardNrRow, GenericRow, AddressComponent } from './Components';

export async function mapContactsToCompanies(
  customerArray: Customer[],
  getContactCompanies: (contactCompanyIds: string[]) => Promise<Customer[]>,
): Promise<Customer[]> {
  const {
    0: customers,
    ...contacts
  }: { [key: string]: Customer[] } = R.groupBy(
    R.prop('companyID'),
    customerArray,
  );
  if (!contacts || R.isEmpty(contacts)) {
    return customerArray;
  }
  const contactCompanyIds = Object.keys(contacts);
  const contactCompanies = await getContactCompanies(contactCompanyIds);

  const mappedCustomers = (customers ?? [])
    .filter(
      customer => !contactCompanies.some(company => company.id === customer.id),
    )
    .concat(
      contactCompanies.map(company => ({
        ...company,
        contactPersons: contacts[company.id],
      })),
    );

  if (contactCompanyIds.length === contactCompanies.length) {
    return mappedCustomers;
  }

  const missingCompanyIds: Customer[] = R.difference(
    contactCompanyIds,
    contactCompanies.map(c => String(c.id)),
  ).reduce(
    (acc: Customer[], id: string) => [...acc, ...contacts[id]],
    mappedCustomers,
  );

  return missingCompanyIds;
}
const companyOrFullName: string = R.ifElse(
  R.propEq('customerType', 'COMPANY'),
  R.prop('companyName'),
  R.compose(R.join(' '), R.props(['firstName', 'lastName'])),
);

const sortByCompanyOrFullName = R.sortBy(
  R.compose(R.toLower, companyOrFullName),
);

export function handleSort(customers: Customer[]) {
  return async (
    _dispatch: ThunkDispatch<RootState, unknown, Action>,
    getState: () => RootState,
  ) => {
    const resultSortingConfig = getCustomerSearchResultsSortingConfiguration(
      getState(),
    );
    // Don't perform any sorting on FE side - when conf is enabled, sorting happens on API level
    if (resultSortingConfig.enabled) {
      return customers;
    }
    return sortByCompanyOrFullName(customers);
  };
}

export function getRowsToDisplay(
  customerResultsConfig: ReturnType<
    typeof getCustomerSearchResultsConfiguration
  >,
  isRetailView: boolean,
) {
  return Object.keys(customerResultsConfig)
    .filter(header => {
      const isEnabled = customerResultsConfig[header] === 1;
      const isRetailHeader = header.includes('retail');
      const shouldDisplay = isRetailView ? isRetailHeader : !isRetailHeader;
      return isEnabled && shouldDisplay;
    })
    .map(header => header.replace('retail_', ''));
}

export function defineComponentToDisplay({
  customer,
  location,
  companyName,
  rowsToDisplay,
}: {
  customer: Customer;
  location?: string;
  companyName?: string;
  rowsToDisplay: ReturnType<typeof getRowsToDisplay>;
}) {
  return rowsToDisplay.map(item => {
    switch (item) {
      case 'name':
        return (
          <NameRow
            key={`${item}-${customer.customerID}`}
            customer={customer}
            companyName={companyName}
            location={location}
          />
        );
      case 'email':
      case 'phone':
      case 'customerID':
      case 'mobile':
      case 'postalCode':
        return (
          <GenericRow
            key={`${item}-${customer.customerID}`}
            customer={customer}
            fieldName={item}
          />
        );
      case 'cardNr':
        return (
          <CardNrRow
            key={`${item}-${customer.customerID}`}
            customer={customer}
          />
        );
      case 'address':
        return (
          <AddressComponent
            key={`${item}-${customer.customerID}`}
            customer={customer}
          />
        );
      default:
        return null;
    }
  });
}
